// Minimal markdown model for AI-generated reports: enough structure (headings,
// paragraphs, lists, tables, rules, bold) to render on screen and to a PDF
// without leaking raw markers like "**" or "|---|".

export interface InlineRun {
  text: string;
  bold?: boolean;
}

export type ReportBlock =
  | { type: 'heading'; level: 1 | 2 | 3; runs: InlineRun[] }
  | { type: 'paragraph'; runs: InlineRun[] }
  | { type: 'list'; ordered: boolean; items: InlineRun[][] }
  | { type: 'table'; header: InlineRun[][]; rows: InlineRun[][][] }
  | { type: 'rule' };

const EMOJI_RE = /\p{Extended_Pictographic}|️|‍|⃣/gu;

export function stripEmoji(text: string): string {
  return text.replace(EMOJI_RE, '').replace(/[ \t]{2,}/g, ' ');
}

export function parseInline(raw: string): InlineRun[] {
  const text = stripEmoji(raw)
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 ($2)');
  const runs: InlineRun[] = [];
  const re = /\*\*([^*]+)\*\*|__([^_]+)__/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (m.index > last) runs.push({ text: text.slice(last, m.index) });
    runs.push({ text: m[1] ?? m[2], bold: true });
    last = m.index + m[0].length;
  }
  if (last < text.length) runs.push({ text: text.slice(last) });
  // Drop leftover single-asterisk emphasis markers.
  return runs
    .map((r) => ({ ...r, text: r.text.replace(/(^|\s)\*(\S[^*]*?)\*(?=\s|$|[.,;:])/g, '$1$2') }))
    .filter((r) => r.text.length > 0);
}

const splitRow = (line: string) =>
  line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());

const isTableSep = (line: string) => /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(line);

export function parseReportMarkdown(source: string): ReportBlock[] {
  const lines = source
    .replace(/\r\n/g, '\n')
    .replace(/^```(?:markdown|md)?\s*\n/i, '')
    .replace(/\n```\s*$/, '')
    .split('\n');
  const blocks: ReportBlock[] = [];
  let para: string[] = [];

  const flushPara = () => {
    if (para.length) {
      blocks.push({ type: 'paragraph', runs: parseInline(para.join(' ')) });
      para = [];
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) { flushPara(); continue; }

    if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmed)) {
      flushPara();
      blocks.push({ type: 'rule' });
      continue;
    }

    const h = /^(#{1,6})\s+(.*)$/.exec(trimmed);
    if (h) {
      flushPara();
      blocks.push({
        type: 'heading',
        level: Math.min(h[1].length, 3) as 1 | 2 | 3,
        runs: parseInline(h[2].replace(/\s+#+$/, '')),
      });
      continue;
    }

    // Table: a pipe row followed by a separator row.
    if (trimmed.includes('|') && i + 1 < lines.length && isTableSep(lines[i + 1])) {
      flushPara();
      const header = splitRow(trimmed).map(parseInline);
      const rows: InlineRun[][][] = [];
      i += 2;
      while (i < lines.length && lines[i].trim().includes('|')) {
        rows.push(splitRow(lines[i]).map(parseInline));
        i++;
      }
      i--;
      blocks.push({ type: 'table', header, rows });
      continue;
    }

    const li = /^(\s*)([-*+•]|\d+[.)])\s+(.*)$/.exec(line);
    if (li) {
      flushPara();
      const ordered = /\d/.test(li[2]);
      const items: InlineRun[][] = [];
      while (i < lines.length) {
        const m = /^(\s*)([-*+•]|\d+[.)])\s+(.*)$/.exec(lines[i]);
        if (!m || /\d/.test(m[2]) !== ordered) break;
        items.push(parseInline(m[3]));
        i++;
      }
      i--;
      blocks.push({ type: 'list', ordered, items });
      continue;
    }

    // A line opening with a bold label ("**Project:** ...") starts its own block.
    if (para.length && trimmed.startsWith('**')) flushPara();
    para.push(trimmed);
  }
  flushPara();
  return blocks;
}

export const runsToText = (runs: InlineRun[]) => runs.map((r) => r.text).join('');
